import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import {
    dicomProcessorState,
    dicomProcessorVersionState,
} from '~/state/atoms/dicomProcessorAtoms';
import { useDomainVersion } from '~/hooks/domain/useDomainVersion';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { collectSeriesNumbers } from '~/domain/services/imageService';

/**
 * Every `SeriesNumber` the session currently holds, across both stores a
 * new series could collide with: the patient aggregate's loaded images and
 * the DicomProcessor's downloaded-but-not-reconstructed series.
 *
 * Both are needed for the same reason the image list merges them — a
 * number free among loaded images may already belong to a raw series
 * sitting beside it. Each carries its own change signal: the aggregate is
 * mutated in place, and the processor cache emits no changeset at all.
 */
export default function useLoadedSeriesNumbers(): ReadonlySet<number> {
    const patient = useSelectedPatient();
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const domainVersion = useDomainVersion();
    const processorVersion = useRecoilValue(dicomProcessorVersionState);

    return useMemo(() => {
        const candidates: unknown[] = [];
        for (const image of patient?.images ?? []) {
            candidates.push(image.seriesMetadata.seriesNumber);
        }
        for (const patientDataSet of dicomProcessor.patients) {
            for (const series of patientDataSet.series) {
                candidates.push(series.seriesModule.seriesNumber);
            }
        }
        return collectSeriesNumbers(candidates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patient, dicomProcessor, domainVersion, processorVersion]);
}
